<template>
  <div v-loading="loading">

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the rate versions"
      description="Please try again in a moment." />

    <template v-else-if="loaded">

      <!-- Rateless: no rate assigned -->
      <el-row v-if="isRateless">
        <el-col :span="24" align="center">
          <span class="text-muted">—</span>
          <div class="text-muted" style="font-size:12px;margin-top:4px;">No rate assigned to this sharer.</div>
        </el-col>
      </el-row>

      <!-- Has versions (pinned or unpinned) -->
      <template v-else>

        <!-- Unpinned: no pinned version -->
        <template v-if="isUnpinned">

          <!-- unpinnedFallback=true + a current published version exists: show tiers -->
          <div v-if="unpinnedFallback && currentVersion">
            <div class="rv-header">
              <span>Version <strong>v{{ currentVersion.version_no }}</strong></span>
              <el-tag type="success" size="small">Current</el-tag>
              <span class="text-muted">Published {{ formatDate(currentVersion.published_at) }}</span>
            </div>
            <div v-if="currentVersion.name" class="text-muted" style="font-size:12px;">{{ currentVersion.name }}</div>
            <!-- XOL-503: currentTierRowData carries THIS version's own band (see the
               computed). The XOL-487 D7 rule still holds — never borrow the
               DRAFT's band for a published version — but the version's own
               band is now on the payload, so it is used. -->
            <rate-tiers-cell :row-data="currentTierRowData" hide-drawer-affordance />
            <div class="text-muted" style="font-size:12px;margin-top:4px;">This sharer isn't pinned — showing the current published version (v{{ currentVersion.version_no }}).</div>
            <div v-if="currentVersionLgHint" class="text-muted rv-lg-hint">{{ currentVersionLgHint }}</div>
          </div>

          <!-- unpinnedFallback=true + zero published versions: unavailable message -->
          <el-row v-else-if="unpinnedFallback && !currentVersion">
            <el-col :span="24" align="center">
              <div class="text-muted" style="font-size:13px;">Tier rates are unavailable for this rate.</div>
            </el-col>
          </el-row>

          <!-- default (unpinnedFallback=false) — reached only by FinanceSharer,
               the one consumer that does not pass the prop.

               XOL-497 (Carlos, 2026-09-13): "Latest (unversioned)" named a
               state the product does not allow, and read like a selectable
               option called "Latest" — the same confusion behind the two rate
               -version picker bugs fixed in this ticket. It is a state
               description, so it now reads as one.

               NOTHING CAN CURRENTLY REACH THIS. It needs a licensee with a
               rate and no pin, and that population is 0 across every status.
               It is also uncreatable since API #252: store() refuses-then-pins,
               update() rejects an explicit null, bulk fails the row, and re-pin
               requires a real version of the licensee's own rate. Only a legacy
               pre-auto-pin row could get here.

               Kept rather than deleted deliberately: removing it leaves this
               v-if/v-else-if chain with no else, so such a row would render a
               BLANK region instead of an explanation. A wrong-ish label beats
               nothing at all.

               The second line stays as-is and is NOT relabelled to mention
               publishing — the API confirmed a rated-but-unpinned licensee
               genuinely does bill off the live rate, so it is accurate for the
               only state that reaches here. -->
          <el-row v-else>
            <el-col :span="24" align="center">
              <i class="fa fa-link text-muted" aria-hidden="true"></i> <i class="text-muted">No pinned version</i>
              <div class="text-muted" style="font-size:12px;margin-top:4px;">This sharer isn't pinned to a published rate version; its fees follow the current rate.</div>
            </el-col>
          </el-row>

        </template>

        <!-- Pinned: show version header + tiers -->
        <div v-else-if="isPinned">
          <div class="rv-header">
            <span>Version <strong>{{ versionLabel }}</strong></span>
            <el-tag :type="pinnedVersion.is_current ? 'success' : 'info'" size="small">{{ pinnedVersion.is_current ? 'Current' : 'Superseded' }}</el-tag>
            <span class="text-muted">Published {{ publishedLabel }}</span>
          </div>
          <div v-if="pinnedVersion && pinnedVersion.name" class="text-muted" style="font-size:12px;">{{ pinnedVersion.name }}</div>
          <!-- XOL-503: tierRowData carries THIS version's own band (see the
               computed). The XOL-487 D7 rule still holds — never borrow the
               DRAFT's band for a published version — but the version's own
               band is now on the payload, so it is used. -->
          <rate-tiers-cell :row-data="tierRowData" hide-drawer-affordance />
          <div v-if="pinnedVersionLgHint" class="text-muted rv-lg-hint">{{ pinnedVersionLgHint }}</div>
        </div>

      </template>

    </template>

  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

import RateTiersCell from '@/components/finance/rates/RateTiersCell'
import { visibleRatePositions } from '@/lib/rateBand'

export default {
  name: 'sharer-rate-version',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    RateTiersCell
  },

  props: {
    id: {
      type: [Number, String],
      required: true
    },

    rateId: {
      type: [Number, String],
      default: null
    },

    // When true and the licensee is unpinned-but-rated, show the is_current
    // published version's tiers instead of the default "No pinned version"
    // message. Default false — keeps per-site FinanceSharer display unchanged.
    unpinnedFallback: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      versions: [],
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    pinnedVersion: function () {
      return this.versions.find(function (v) { return v.is_pinned }) || null
    },

    // The is_current published version from the fetched list — used by the
    // unpinned-fallback branch to show the current published version's tiers.
    currentVersion: function () {
      return this.versions.find(function (v) { return v.is_current }) || null
    },

    isPinned: function () {
      return this.loaded && !this.errored && !!this.pinnedVersion
    },

    isUnpinned: function () {
      return this.loaded && !this.errored && !!this.rateId && !this.pinnedVersion
    },

    isRateless: function () {
      return this.loaded && !this.errored && !this.rateId
    },

    versionLabel: function () {
      return this.pinnedVersion ? 'v' + this.pinnedVersion.version_no : ''
    },

    publishedLabel: function () {
      return (this.pinnedVersion && this.pinnedVersion.published_at)
        ? this.formatDate(this.pinnedVersion.published_at)
        : '—'
    },

    // XOL-503: the pinned version's OWN band travels with its tiers. Returns
    // the version object itself rather than a picked literal, so a version
    // predating XOL-500 (no band keys) degrades to rateBand's [1..10] default
    // exactly as before.
    tierRowData: function () {
      return this.pinnedVersion || { tiers: [] }
    },

    // XOL-487: promoted from the inline object literal in the template
    // (mirrors tierRowData above) for the unpinned-fallback branch.
    // XOL-503: same treatment — the current version's own band.
    currentTierRowData: function () {
      return this.currentVersion || { tiers: [] }
    },

    // XOL-503 — these rows now DO carry a band: each version's own, straight
    // off the payload, which has included fromSharer/toSharer since XOL-500
    // (API c0bcf804).
    //
    // The XOL-487 D7 rule stands and is the reason this took a second change
    // rather than one: `rate_versions` snapshots its own band because a
    // published version's band can legitimately differ from the draft's, so
    // borrowing the DRAFT's band would render an authoritative-looking wrong
    // band. Only the version's own band is safe, and now it is available.
    //
    // The previous revision of this comment claimed the hint would "stay
    // correct with zero further SPA changes if the API ever widens these two
    // endpoints". That was FALSE and is the whole reason XOL-503 exists: the
    // band-less literals discarded the new fields one line after they
    // arrived, so the API change landed invisibly. Widening a payload does
    // not update a consumer that throws the keys away.
    currentVersionLgHint: function () {
      return this.lgHintFor(this.currentTierRowData)
    },

    pinnedVersionLgHint: function () {
      return this.lgHintFor(this.tierRowData)
    }
  },

  watch: {
    rateId: function () {
      this.load()
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    // XOL-487 — builds the responsive-hint sentence in JS, not the template.
    // `first` is the leftmost lg-only rendered position (rendered index 3);
    // `last` is the last rendered position. Deriving from visibleRatePositions
    // (NOT the raw band) is a UX-spec correction to the plan: using the band
    // would, for e.g. a 3-10 rate, promise "#6-#10 shown on wider screens"
    // when #9/#10 are drawer-only and never shown in this cell at any width.
    // Returns '' when nothing is lg-only (band fits in <=3 positions) — the
    // caller's v-if hides the element entirely rather than rendering empty.
    lgHintFor: function (row) {
      var visible = visibleRatePositions(row)
      if (visible.length <= 3) return ''

      var first = visible[3]
      var last = visible[visible.length - 1]

      // Singular when exactly one position is lg-only ("Rate #5" not
      // "Rates #5–#5") — reachable via the form (e.g. a 2-5 band).
      if (first === last) {
        return 'Rate #' + first + ' shown on wider screens.'
      }
      // En-dash is a literal U+2013 character — an HTML entity would not
      // survive interpolation (mirrors RateTiersCell.vue's tenorLabel()).
      // Both numbers keep their '#'.
      return 'Rates #' + first + '–#' + last + ' shown on wider screens.'
    },

    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/rate-versions',
        function (data) {
          self.versions = Array.isArray(data) ? data : []
          self.loading = false
          self.loaded = true
        },
        'rate versions',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
        }
      )
    }
  }
}
</script>

<style scoped>
.rv-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.rv-lg-hint {
  font-size: 11px;
  margin-top: 4px;
}

@media (min-width: 1200px) {
  .rv-lg-hint { display: none; }
}
</style>
